import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { urlMapper } from '@shared/models';
import { WindowRef } from '@shared/services/window-ref/window-ref.service';
import {
  selectInheritedIraContactInfoNextPage,
  selectInheritedIraDecedentInfoNextPage,
  selectInheritedIraEmploymentNextPage,
  selectInheritedIraIncomeNetWorthNextPage,
  selectInheritedIraInvestmentObjNextPage,
  selectInheritedIraLetterUploadNextPage,
  selectInheritedIraPersonalInfoNextPage,
  selectInheritedIraReviewNextPage,
  selectInheritedIraSettlementFundNextPage,
  selectInheritedIraTermsNewNextPage,
} from '@store/selectors/router/inherited-ira/inherited-ira-router.selectors';
import {
  delay,
  exhaustMap,
  filter,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import * as RouterActions from '../../actions/router/router.actions';
import * as fromRootStore from '../../index';
import { RouterStateUrl } from '../../index';
import {
  selectRouterState,
  selectTestMonkeyNextPage,
} from '../../selectors/router/common/router.selectors';
import {
  selectGiftingAdditionalInstructionsNextPage,
  selectGiftingAssetsNextPage,
  selectGiftingMethodNextPage,
  selectGiftingOverviewNextPage,
  selectGiftingRecipientNextPage,
  selectGiftingRecipientTriageNextPage,
  selectGiftingReviewNextPage,
  selectGiftingTermsNextPage,
  selectGiftingTriageNextPage,
} from '../../selectors/router/gifting/gifting-router.selectors';
import {
  selectInheritanceAssumeNextPage,
  selectInheritanceBeneDetailsNextPage,
  selectInheritanceBeneRelationshipNextPage,
  selectInheritanceContactInfoNextPage,
  selectInheritanceDecedentInfoNextPage,
  selectInheritanceEmploymentNextPage,
  selectInheritanceEstateNextPage,
  selectInheritanceIncomeNetWorthNextPage,
  selectInheritanceInvestObjNextPage,
  selectInheritanceIraTypeNextPage,
  selectInheritanceLetterUploadNextPage,
  selectInheritanceOverviewNextPage,
  selectInheritancePersonalInfoNextPage,
  selectInheritanceReviewNextPage,
  selectInheritanceTCFTrustInfoNextPage,
  selectInheritanceTCFTrustSignNextPage,
  selectInheritanceTermsNextPage,
  selectInheritanceTransferFromNextPage,
  selectInheritanceTransferToNextPage,
  selectInheritanceTriageNextPage,
  selectInheritanceTrustInfoNextPage,
  selectInheritanceTrustSettlementFundNextPage,
  selectInheritanceTrustTaxInfoNextPage,
  selectInheritanceUploadNextPage,
  selectMultiInheritanceOverviewNextPage,
} from '../../selectors/router/inheritance/inheritance-router.selectors';
import {
  selectDivorceAmountsNextPage,
  selectDivorceCostBasisElectionNextPage,
  selectDivorceDocUploadNextPage,
  selectDivorceFromToNextPage,
  selectDivorceMultiReviewNextPage,
  selectDivorceReviewHoldingsNextPage,
  selectDivorceReviewNextPage,
  selectDivorceTermsNextPage,
  selectDivorceTransferDetailsNextPage,
  selectInternalTransferAmountsNextPage,
  selectInternalTransferContactInfoNextPage,
  selectInternalTransferCostBasisElectionMultiNextPage,
  selectInternalTransferCostBasisElectionNextPage,
  selectInternalTransferDetailsNextPage,
  selectInternalTransferEmploymentNextPage,
  selectInternalTransferFromToNextPage,
  selectInternalTransferHoldingsReviewNextPage,
  selectInternalTransferIncomeNetWorthNextPage,
  selectInternalTransferInvestObjNextPage,
  selectInternalTransferLetterUploadNextPage,
  selectInternalTransferMultiReviewNextPage,
  selectInternalTransferNewAccountReviewNextPage,
  selectInternalTransferOverviewNextPage,
  selectInternalTransferPersonalInfoNextPage,
  selectInternalTransferReviewNextPage,
  selectInternalTransferSettlementFundNextPage,
  selectInternalTransferTermsExistingNextPage,
  selectInternalTransferTriageMultiNextPage,
  selectInternalTransferTriageNextPage,
} from '../../selectors/router/internal-transfer/internal-transfer-router.selectors';
import {
  selectMinorContactInfoNextPage,
  selectMinorEmploymentNextPage,
  selectMinorIncomeNetWorthNextPage,
  selectMinorInvestObjNextPage,
  selectMinorLetterUploadNextPage,
  selectMinorPersonalInfoNextPage,
  selectMinorReviewNextPage,
  selectMinorSettlementFundNextPage,
  selectMinorTermsNextPage,
  selectMinorTransferFromNextPage,
  selectMinorTransferToNextPage,
} from '../../selectors/router/minor-transfer/minor-transfer-router.selectors';
import {
  selectMultiInheritanceAccountTriageNextPage,
  selectMultiInheritanceContactInfoNextPage,
  selectMultiInheritanceDecedentInfoNextPage,
  selectMultiInheritanceEmploymentNextPage,
  selectMultiInheritanceEstateNextPage,
  selectMultiInheritanceIncomeNetWorthNextPage,
  selectMultiInheritanceInvestObjNextPage,
  selectMultiInheritanceL8ItwUploadNextPage,
  selectMultiInheritanceLetterUploadNexPage,
  selectMultiInheritanceNewAccountReviewNextPage,
  selectMultiInheritancePersonalInfoNextPage,
  selectMultiInheritanceReviewNextPage,
  selectMultiInheritanceSettlementFundNextPage,
  selectMultiInheritanceTermsNextPage,
  selectMultiInheritanceTriageNextPage,
  selectMultiInheritanceTrustInfoNextPage,
  selectMultiInheritanceTrustTaxInfoNextPage,
  selectMultiInheritanceTrustTriageNextPage,
  selectMultiInheritanceUploadNextPage,
} from '../../selectors/router/multi-inheritance/multi-inheritance-router.selectors';
import {
  selectDAEnrollNextPage,
  selectDAOfferNextPage,
  selectInvestmentStrategyNextPage,
  selectMultiAccountEnrollmentNextPage,
  selectPortfolioMatchNextPage,
  selectRiskQuizResultsNextPage,
  selectRiskQuizSetupNextPage,
  selectTaxFilingRetirementNextPage,
} from '../../selectors/router/open-choice/open-choice-router.selectors';
import { selectTriageState } from '../../selectors/triage/triage.selectors';

@Injectable()
export class RouterEffects {
  private isNavigating = false;

  constructor(
    private actions$: Actions,
    private router: Router,
    private location: Location,
    private globalStore: Store<fromRootStore.State>,
    private winRef: WindowRef,
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        delay(1000),
      )
      .subscribe(() => {
        this.isNavigating = false;
      });
  }

  routeMap = {
    '/secure/testmonkey': selectTestMonkeyNextPage,
    inheritance: {
      // inheritance public pages
      guide: selectInheritanceOverviewNextPage, // not necessary but leaving now
      overview: selectInheritanceOverviewNextPage, // setting us up to change on public site
      triage: selectInheritanceTriageNextPage,
      'trust-triage': selectInheritanceTriageNextPage,
      // inheritance secure pages
      'decedent-info': selectInheritanceDecedentInfoNextPage,
      upload: selectInheritanceUploadNextPage,
      assume: selectInheritanceAssumeNextPage,
      'transfer-from': selectInheritanceTransferFromNextPage,
      'transfer-to': selectInheritanceTransferToNextPage,
      'ira-type': selectInheritanceIraTypeNextPage,
      relationship: selectInheritanceBeneRelationshipNextPage,
      details: selectInheritanceBeneDetailsNextPage,
      estate: selectInheritanceEstateNextPage,
      'personal-info': selectInheritancePersonalInfoNextPage,
      employment: selectInheritanceEmploymentNextPage,
      letter: selectInheritanceLetterUploadNextPage,
      'contact-info': selectInheritanceContactInfoNextPage,
      'investment-objectives': selectInheritanceInvestObjNextPage,
      'income-net-worth': selectInheritanceIncomeNetWorthNextPage,
      review: selectInheritanceReviewNextPage,
      existing: selectInheritanceTermsNextPage,
      new: selectInheritanceTermsNextPage,
      'review-sign': selectInheritanceTermsNextPage,
      'trust-info': selectInheritanceTrustInfoNextPage,
      'trust-tax-info': selectInheritanceTrustTaxInfoNextPage,
      'tcf-info-trust': selectInheritanceTCFTrustInfoNextPage,
      'tcf-sign-trust': selectInheritanceTCFTrustSignNextPage,
      'settlement-fund': selectInheritanceTrustSettlementFundNextPage,
    },
    gifting: {
      // gifting public pages
      overview: selectGiftingOverviewNextPage, // setting us up to change on public site
      'gifting-welcome': selectGiftingOverviewNextPage,
      triage: selectGiftingTriageNextPage,
      // gifting secure pages
      assets: selectGiftingAssetsNextPage,
      method: selectGiftingMethodNextPage,
      'additional-instructions': selectGiftingAdditionalInstructionsNextPage,
      recipient: selectGiftingRecipientNextPage,
      'recipient-triage': selectGiftingRecipientTriageNextPage,
      review: selectGiftingReviewNextPage,
      'review-multisigner': selectGiftingReviewNextPage,
      'gift-existing': selectGiftingTermsNextPage,
      'gift-existing-multisigner': selectGiftingTermsNextPage,
      // summary and method have their own effects for navigation
    },
    'internal-transfer': {
      overview: selectInternalTransferOverviewNextPage,
      triage: selectInternalTransferTriageNextPage,
      'transfer-details': selectInternalTransferDetailsNextPage,
      'holdings-review': selectInternalTransferHoldingsReviewNextPage,
      'holdings-review-multisigner': selectInternalTransferHoldingsReviewNextPage,
      'personal-info': selectInternalTransferPersonalInfoNextPage,
      'contact-info': selectInternalTransferContactInfoNextPage,
      'settlement-fund': selectInternalTransferSettlementFundNextPage,
      employment: selectInternalTransferEmploymentNextPage,
      letter: selectInternalTransferLetterUploadNextPage,
      'income-net-worth': selectInternalTransferIncomeNetWorthNextPage,
      'investment-objectives': selectInternalTransferInvestObjNextPage,
      review: selectInternalTransferReviewNextPage,
      'review-multisigner': selectInternalTransferReviewNextPage,
      'cost-basis-election': selectInternalTransferCostBasisElectionNextPage,
      existing: selectInternalTransferTermsExistingNextPage,
      'existing-multisigner': selectInternalTransferTermsExistingNextPage,
      new: selectInternalTransferTermsExistingNextPage,
      'overview-alt': selectInternalTransferOverviewNextPage,
      // Multi-receiving flow pages
      'multi-triage': selectInternalTransferTriageMultiNextPage,
      'multi-from-to': selectInternalTransferFromToNextPage,
      'multi-cost-basis-election': selectInternalTransferCostBasisElectionMultiNextPage,
      'multi-amounts': selectInternalTransferAmountsNextPage,
      'multi-review': selectInternalTransferMultiReviewNextPage,
      'multi-review-multisigner': selectInternalTransferMultiReviewNextPage,
      'new-account-review': selectInternalTransferNewAccountReviewNextPage,
    },
    'minor-transfer': {
      'transfer-from': selectMinorTransferFromNextPage,
      'transfer-to': selectMinorTransferToNextPage,
      'personal-info': selectMinorPersonalInfoNextPage,
      'contact-info': selectMinorContactInfoNextPage,
      'settlement-fund': selectMinorSettlementFundNextPage,
      employment: selectMinorEmploymentNextPage,
      letter: selectMinorLetterUploadNextPage,
      'investment-objectives': selectMinorInvestObjNextPage,
      'income-net-worth': selectMinorIncomeNetWorthNextPage,
      review: selectMinorReviewNextPage,
      existing: selectMinorTermsNextPage,
      new: selectMinorTermsNextPage,
      // open choice routes
      enroll: selectDAEnrollNextPage,
      offer: selectDAOfferNextPage,
      'tax-filing-retirement': selectTaxFilingRetirementNextPage,
      'risk-quiz-setup': selectRiskQuizSetupNextPage,
      'risk-quiz-results': selectRiskQuizResultsNextPage,
      'portfolio-match': selectPortfolioMatchNextPage,
      'multi-account-enrollment': selectMultiAccountEnrollmentNextPage,
      'investment-strategy': selectInvestmentStrategyNextPage,
    },
    'multi-inheritance': {
      // public pages
      triage: selectMultiInheritanceTriageNextPage,
      'trust-triage': selectMultiInheritanceTrustTriageNextPage,
      overview: selectMultiInheritanceOverviewNextPage,
      // secure pages
      'decedent-info': selectMultiInheritanceDecedentInfoNextPage,
      'decedent-information': selectMultiInheritanceDecedentInfoNextPage,
      upload: selectMultiInheritanceUploadNextPage,
      'account-triage': selectMultiInheritanceAccountTriageNextPage,
      estate: selectMultiInheritanceEstateNextPage,
      'personal-info': selectMultiInheritancePersonalInfoNextPage,
      'contact-info': selectMultiInheritanceContactInfoNextPage,
      employment: selectMultiInheritanceEmploymentNextPage,
      letter: selectMultiInheritanceLetterUploadNexPage,
      'income-net-worth': selectMultiInheritanceIncomeNetWorthNextPage,
      'investment-objectives': selectMultiInheritanceInvestObjNextPage,
      'new-account-review': selectMultiInheritanceNewAccountReviewNextPage,
      review: selectMultiInheritanceReviewNextPage,
      existing: selectMultiInheritanceTermsNextPage,
      'multi-new': selectMultiInheritanceTermsNextPage,
      'review-sign': selectMultiInheritanceTermsNextPage,
      'settlement-fund': selectMultiInheritanceSettlementFundNextPage,
      'trust-info': selectMultiInheritanceTrustInfoNextPage,
      'trust-tax-info': selectMultiInheritanceTrustTaxInfoNextPage,
      'L8-upload': selectMultiInheritanceL8ItwUploadNextPage,
      'ITW-upload': selectMultiInheritanceL8ItwUploadNextPage,
    },
    'inherited-ira': {
      // secure pages
      'decedent-info': selectInheritedIraDecedentInfoNextPage,
      'personal-info': selectInheritedIraPersonalInfoNextPage,
      'contact-info': selectInheritedIraContactInfoNextPage,
      'settlement-fund': selectInheritedIraSettlementFundNextPage,
      employment: selectInheritedIraEmploymentNextPage,
      letter: selectInheritedIraLetterUploadNextPage,
      'income-net-worth': selectInheritedIraIncomeNetWorthNextPage,
      'investment-objectives': selectInheritedIraInvestmentObjNextPage,
      review: selectInheritedIraReviewNextPage,
      new: selectInheritedIraTermsNewNextPage,
    },
    divorce: {
      'multi-triage': selectInternalTransferTriageMultiNextPage,
      upload: selectDivorceDocUploadNextPage,
      'transfer-details': selectDivorceTransferDetailsNextPage,
      'holdings-review': selectDivorceReviewHoldingsNextPage,
      review: selectDivorceReviewNextPage,
      'from-to': selectDivorceFromToNextPage,
      amounts: selectDivorceAmountsNextPage,
      'cost-basis-election': selectDivorceCostBasisElectionNextPage,
      'multi-review': selectDivorceMultiReviewNextPage,
      'divorce-existing': selectDivorceTermsNextPage,
    },
  };

  navigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.Go),
        exhaustMap(({ path, query: queryParams, extras }) =>
          from(this.router.navigate(path, { queryParams, ...extras })).pipe(take(1)),
        ),
      ),
    { dispatch: false },
  );

  navigateBack$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.Back),
        tap(() => this.location.back()),
      ),
    { dispatch: false },
  );

  navigateForward$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.Forward),
        tap(() => this.location.forward()),
      ),
    { dispatch: false },
  );

  navigateNext$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.Next),
        filter(() => !this.isNavigating),
        tap(() => (this.isNavigating = true)),
        withLatestFrom(
          this.globalStore.pipe(select(selectRouterState)),
          this.globalStore.pipe(select(selectTriageState)),
        ),
        exhaustMap(([, routerState, triageState]) => {
          const currentPage = this.getCurrentPage(routerState);
          const flow = urlMapper[triageState.flowType];
          return of(currentPage).pipe(
            withLatestFrom(this.globalStore.pipe(select(this.routeMap[flow][currentPage]))),
            switchMap(([, nextPage]) => from(this.router.navigate([nextPage])).pipe(take(1))),
          );
        }),
      ),
    { dispatch: false },
  );

  navigateExternal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.External),
        map((payload) => {
          this.winRef.nativeWindow.location.href = payload.url;
        }),
      ),
    { dispatch: false },
  );

  getCurrentPage(routerState: RouterStateUrl): string {
    /**
     * We need this logic because we have a route with a parameter at the moment and we might have others in the future.
     * However, this assumes that the params are at the end of the route configuration. If we will have any route with params
     * In the middle of the route, we will need to revisit this logic.
     */
    const currentDomain = routerState.url.split('?')[0].split('/');
    const lastIndex = currentDomain.length - 1;
    const paramsCount = Object.keys(routerState.params).length;
    return currentDomain[lastIndex - paramsCount];
  }
}
